// active-worker-record.ts
//
// Phase 05 (AC-02, B1 final_rescan): `<control_root>/active-worker.json`
// records the worker `claude` process a supervisor spawned, so `avatar
// autopilot unlock` can kill an orphaned worker group after a hard SIGKILL
// of the supervisor. `*_started_at` fields MUST be the exact `ps -o lstart=`
// string from LockLivenessProbe.processStartedAt — never `toISOString()` —
// or unlock could never confirm pid identity against a reused pid.
import { mkdirSync, readFileSync, renameSync, unlinkSync, writeFileSync } from "node:fs";
import { hostname } from "node:os";
import { join } from "node:path";
import type { SpawnFn } from "./claude-process-group-spawner.js";
import { type LockLivenessProbe, realLivenessProbe } from "./process-liveness-probe.js";

export interface ActiveWorkerRecord {
  pid: number;
  pgid: number;
  supervisor_pid: number;
  supervisor_started_at?: string;
  worker_started_at?: string;
  spawned_at: string;
  /** 06g R2b: pids only mean something on the host that spawned them; absent on records written before this field. */
  hostname?: string;
}

function recordPath(controlRoot: string): string {
  return join(controlRoot, "active-worker.json");
}

function isValidRecord(v: unknown): v is ActiveWorkerRecord {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    Number.isInteger(r.pid) &&
    (r.pid as number) > 1 &&
    Number.isInteger(r.pgid) &&
    (r.pgid as number) > 1 &&
    Number.isInteger(r.supervisor_pid) &&
    (r.supervisor_pid as number) > 1
  );
}

export function writeActiveWorkerSync(controlRoot: string, record: ActiveWorkerRecord): void {
  mkdirSync(controlRoot, { recursive: true });
  const path = recordPath(controlRoot);
  const tmp = `${path}.tmp`;
  writeFileSync(tmp, JSON.stringify(record), { mode: 0o600 });
  renameSync(tmp, path);
}

export async function readActiveWorker(
  controlRoot: string,
): Promise<ActiveWorkerRecord | undefined> {
  try {
    const parsed: unknown = JSON.parse(readFileSync(recordPath(controlRoot), "utf8"));
    return isValidRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function clearActiveWorkerSync(controlRoot: string, expectedPid?: number): void {
  const path = recordPath(controlRoot);
  try {
    if (expectedPid !== undefined) {
      const onDisk: unknown = JSON.parse(readFileSync(path, "utf8"));
      if (!isValidRecord(onDisk) || onDisk.pid !== expectedPid) return;
    }
    unlinkSync(path);
  } catch {
    // ENOENT or unreadable — nothing to clear.
  }
}

export interface WrapSpawnRecordingActiveWorkerInput {
  spawn: SpawnFn;
  controlRoot: string;
  liveness?: LockLivenessProbe;
  supervisorStartedAt: string | undefined;
}

export interface WrapSpawnRecordingActiveWorkerResult {
  spawn: SpawnFn;
  current: () => { pid: number; pgid: number } | undefined;
}

export function wrapSpawnRecordingActiveWorker(
  input: WrapSpawnRecordingActiveWorkerInput,
): WrapSpawnRecordingActiveWorkerResult {
  const { controlRoot, supervisorStartedAt } = input;
  const liveness = input.liveness ?? realLivenessProbe;
  let current: { pid: number; pgid: number } | undefined;

  const spawn: SpawnFn = ((command: string, args: unknown, options: unknown) => {
    const child = (input.spawn as (...a: unknown[]) => ReturnType<SpawnFn>)(command, args, options);
    if (command !== "claude") return child;
    const pid = (child as { pid?: number }).pid;
    if (!Number.isInteger(pid) || (pid as number) <= 1) return child;

    const rec: ActiveWorkerRecord = {
      pid: pid as number,
      pgid: pid as number,
      supervisor_pid: process.pid,
      ...(supervisorStartedAt !== undefined ? { supervisor_started_at: supervisorStartedAt } : {}),
      spawned_at: new Date().toISOString(),
      hostname: hostname(),
    };
    writeActiveWorkerSync(controlRoot, rec);
    current = { pid: rec.pid, pgid: rec.pgid };

    (child as { once: (ev: string, cb: () => void) => void }).once("exit", () => {
      current = undefined;
      clearActiveWorkerSync(controlRoot, rec.pid);
    });

    liveness
      .processStartedAt(rec.pid)
      .then((startedAt) => {
        if (startedAt && current?.pid === rec.pid) {
          writeActiveWorkerSync(controlRoot, { ...rec, worker_started_at: startedAt });
        }
      })
      .catch(() => {});

    return child;
    // biome-ignore lint/suspicious/noExplicitAny: matching node:child_process.spawn's broad overload set
  }) as any as SpawnFn;

  return { spawn, current: () => current };
}
